import React, { FunctionComponent, useMemo, useState } from 'react'
import { Alert, Button, Card, Form, InputNumber, notification, Typography } from 'antd'

import { TExpertProfile, TExpertProfileRequest } from '../../../../../context/types'
import { useExpertProfilesContext } from '../../../../../context/expertProfile'
import { useAccountContext } from '../../../../../context/account'
import SpinLoader from '../../../../helpers/SpinLoader'

import './ExpertRateForm.less'
import { currentBrand } from '../../../../../config'
import { amountToCurrencyFormat } from '../../../../../utils/format'

const { Paragraph } = Typography

type TProps = {
	expertProfile?: TExpertProfile
}

const ExpertRateForm: FunctionComponent<TProps> = ({ expertProfile }) => {
	const [form] = Form.useForm()
	const [loading, setLoading] = useState<boolean>(false)
	const [formErrors, setFormErrors] = useState<any[]>([])
	const { actions } = useExpertProfilesContext()
	const { account } = useAccountContext()

	const initialValues = { pay_rate: (expertProfile.pay_rate ??= 0) / 100 }

	const onFinish = async (values: TExpertProfileRequest) => {
		values.pay_rate *= 100
		setLoading(true)
		try {
			const response = await actions.updateExpertProfile(expertProfile.id, values, account?.token)
			if (response.ok) {
				notification.success({
					message: 'Successfully Saved',
				})
			}
		} finally {
			setLoading(false)
		}
	}

	const onFieldsChange = () => {
		const nextFormErrors = form.getFieldsError().filter((v) => v.errors.length)
		setFormErrors(nextFormErrors)
	}

	return useMemo(() => {
		return (
			<SpinLoader loading={loading}>
				<Card className="expert-form-section" bordered={false}>
					<p>
						For calls not booked through a corporate program, you can set your own rate for a 50 minute call. What would
						you like that rate to be?
					</p>
					<Form
						name="expert_rate"
						form={form}
						layout="inline"
						initialValues={initialValues}
						onFinish={onFinish}
						autoComplete="off"
						onFieldsChange={onFieldsChange}
					>
						<Form.Item
							label="Pay Rate $"
							name="pay_rate"
							rules={[
								{
									validator: (_rule, value) => {
										if (
											!currentBrand.creditBased ||
											(value > 0 && !(value % +amountToCurrencyFormat(currentBrand.creditAmount)))
										) {
											return Promise.resolve()
										}
										return Promise.reject(
											`The expert rate should be multiple of 1 credit - $${amountToCurrencyFormat(
												currentBrand.creditAmount
											)}!`
										)
									},
									message: `The expert rate should be multiple of 1 credit - $${amountToCurrencyFormat(
										currentBrand.creditAmount
									)}!`,
								},
								{ required: true, message: 'Please input Pay Rate!' },
							]}
						>
							<InputNumber min={0} />
						</Form.Item>

						<Form.Item>
							<Button type="primary" htmlType="submit" shape="round" size="middle" loading={loading}>
								Save
							</Button>
						</Form.Item>
					</Form>
					{formErrors.length > 0 && (
						<Alert
							showIcon
							type="warning"
							message="Warning"
							description={formErrors.map((v) => (
								<Paragraph key={v.name + '_errors'}>{v.errors[0]}</Paragraph>
							))}
						/>
					)}
				</Card>
			</SpinLoader>
		)
	}, [expertProfile.pay_rate, expertProfile.id, loading, formErrors.length])
}

export default ExpertRateForm
